/**
 * Stable diagnostic codes, like rustc's E-codes. Agents key behavior and
 * memory on codes, so a code's meaning never changes; retire and add instead.
 * Families: F automation structure, T types, E error handling, P policies,
 * H hosts/effects, U unsafe, L lints, M migrations, S script text.
 */
export interface CodeInfo {
  code: string;
  title: string;
  explanation: string;
}

const CODES: CodeInfo[] = [
  {
    code: "S0001",
    title: "script syntax error",
    explanation:
      "The AutomationScript doesn't parse. Fix the text at the line and column the message gives; see guide/script for the grammar.",
  },
  {
    code: "S0002",
    title: "Function code doesn't type-check",
    explanation:
      "A core.function step's code block fails TypeScript checks against the step's input query and Output. Fix the code or the step's inputQuery/outputSDL; see steps/core.function.",
  },
  {
    code: "S0003",
    title: "test statements aren't saved",
    explanation:
      "Committing a workspace saves the automation only; `test` statements are dropped from what's stored.",
  },
  {
    code: "F0001",
    title: "invalid automation document",
    explanation:
      "The automation doesn't match the AutomationDefinition schema. Fix the field the message names.",
  },
  {
    code: "F0002",
    title: "unknown trigger",
    explanation:
      "The trigger kind isn't in the registry. Pick a trigger from registry.triggers.",
  },
  {
    code: "F0003",
    title: "kind version mismatch",
    explanation:
      "The node was written against another version of its kind. Apply the suggested migration (M fixes upgrade the config mechanically).",
  },
  {
    code: "F0004",
    title: "invalid step config",
    explanation:
      "The step's config fails its kind's schema or cross-field check.",
  },
  {
    code: "F0005",
    title: "trigger not offered",
    explanation:
      "The trigger still runs but can't be chosen for new automations.",
  },
  {
    code: "F0006",
    title: "duplicate node id",
    explanation: 'Node ids must be unique and must not be "trigger".',
  },
  {
    code: "F0007",
    title: "unknown step kind",
    explanation: "The node's kind isn't in the registry.",
  },
  {
    code: "F0008",
    title: "invalid edge",
    explanation:
      "The edge points at a missing node, a missing port, or a port that already has an edge. Each port has at most one edge.",
  },
  {
    code: "F0009",
    title: "cycle",
    explanation:
      "Automations are acyclic so every path can be typed and explored. Use a structured step (wait, rewind) instead of an edge back.",
  },
  {
    code: "T0001",
    title: "type mismatch",
    explanation:
      "The expression's type doesn't fit where it's used, or references a path that doesn't exist at this step. Use lang.scope(nodeId) to see what's available.",
  },
  {
    code: "T0002",
    title: "may not have run",
    explanation:
      "The expression reads output from a step that doesn't run on every path to here (or can be skipped, or can fail onto its error port). Wrap it in coalesce(...) or move this step after a point all paths share.",
  },
  {
    code: "T0003",
    title: "nullable value where a value is required",
    explanation:
      "Like passing Option<T> where T is expected: handle the missing case with coalesce(value, default) or guard with isSet(...).",
  },
  {
    code: "T0004",
    title: "non-exhaustive branch",
    explanation:
      "A switch over an enum doesn't handle every value and has no default. Add the missing cases or a default port.",
  },
  {
    code: "B0001",
    title: "action target not bound",
    explanation:
      "The action needs to know which record it acts on (a value of the record's id type). Fixes list every value of that type at this step; with exactly one, `fix` applies it. With none, the trigger can't reach this action here.",
  },
  {
    code: "E0001",
    title: "unhandled failure",
    explanation:
      "This step can fail and its error port isn't connected, so a failure ends the run (like `?` propagating to main). Connect the error port to handle it, or accept that failures end the run.",
  },
  {
    code: "U0001",
    title: "unsafe step",
    explanation:
      "A step kind marked unsafe: an escape hatch the checker can't see into. Prefer a typed kind; keep its outputs declared.",
  },
  {
    code: "U0002",
    title: "unchecked access into untyped data",
    explanation:
      "The path continues into JSON, so the rest isn't type-checked. A typo here fails silently (reads null).",
  },
  {
    code: "L0001",
    title: "unreachable step",
    explanation:
      "No path from the trigger reaches this step. Connect it or remove it.",
  },
  {
    code: "L0002",
    title: "unused output",
    explanation:
      "This pure step only exists to produce output, and nothing reads it.",
  },
  {
    code: "L0003",
    title: "constant condition",
    explanation:
      "The condition doesn't read any data, so one branch can never run.",
  },
  {
    code: "L0004",
    title: "stale after wait",
    explanation:
      "A step reads output produced before a wait. That data was true when produced and may be stale now; looked-up fields are refetched after waits but step outputs are not.",
  },
  {
    code: "L0008",
    title: "disabled step still runs",
    explanation:
      "The step is marked disabled, but its kind can't be skipped, so runs still execute it. Remove the step, or clear `disabled`.",
  },
  {
    code: "L0007",
    title: "not a value of the enum",
    explanation:
      "Text compared with an enum field isn't one of its values, so the comparison is never true. `fields`/`scope` list each enum's values.",
  },
  {
    code: "L0006",
    title: "impossible path",
    explanation: "The path explorer proved no input reaches this branch.",
  },
];

const BY_VALIDATOR: Record<string, string> = {
  schema: "F0001",
  "unknown-trigger": "F0002",
  "kind-version": "F0003",
  config: "F0004",
  "trigger-gated": "F0005",
  "duplicate-id": "F0006",
  "unknown-kind": "F0007",
  edge: "F0008",
  cycle: "F0009",
  type: "T0001",
  "maybe-missing": "T0002",
  unreachable: "L0001",
  "disabled-not-skippable": "L0008",
};

const INFO = new Map(CODES.map((c) => [c.code, c]));

export function codeFor(validatorCode: string): string {
  return BY_VALIDATOR[validatorCode] ?? "F0000";
}

/** Explains a diagnostic code, like a compiler's `--explain`. */
export function explainCode(code: string): CodeInfo | null {
  return INFO.get(code) ?? null;
}

export function allCodes(): CodeInfo[] {
  return [...CODES];
}
